import { Button, DatePicker, Divider, Dropdown, Empty, Form, Input, InputNumber, MenuProps, Row, Select, Space, Typography } from 'antd'
import dayjs from 'dayjs'

import * as Styles from './styles'
import { ItemCard } from './ItemCard';
import { ServiceContract } from 'src/common/models/contract';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/state/reducer';
import { formatToCurrency, getInputRequiredRule } from 'src/common/util';
import { Col12 } from 'src/components/Columns';
import { useEffect, useState } from 'react';
import { getCustomersApi } from 'src/features/Customer/state/action';
import { Customer } from 'src/common/models/customer';
import { SystemConstants } from 'src/common/constants';
import { cleanCurrentContract, createServiceContractApi, getServiceContractByIdApi, updateCurrentContract, updateServiceContractApi } from '../../state/action';
import { DownOutlined, CreditCardOutlined, UserAddOutlined, EditOutlined, CloseOutlined, MinusSquareOutlined } from '@ant-design/icons';
import { CreateTicketCustomer } from './CreateTicketCustomer';
import { CreatePayment } from './CreatePayment';
import { notification } from "antd"
import { ContractConfirmation } from '../ContractConfirmation';
import { Loader } from 'src/components/Loader';
import { serviceLoading } from 'src/common/apiLoader/state/selection';
import { CREATE_SERVICE_CONTRACT_API, UPDATE_SERVICE_CONTRACT_API } from '../../state/actionTypes';
import { GET_CONTRACT_BY_ID_API } from '../../state/actionTypes';
import { ModalSuccess } from 'src/components/ModalSuccess';
import { useNavigate, useParams } from 'react-router';
import { Urls } from 'src/common/urls';
import { SystemDescriptions } from 'src/common/descriptions';
import { CancelConfirmationModal } from 'src/common/descriptions/components/cancelConfirmationModal';
import { DestroyConfirmationModal } from 'src/components/DestroyConfirmationModal';
import { ActivityLogAction } from 'src/common/models/activityLog';
import { ServiceContractError, ServiceContractErrorOrigin } from '../../state/types';

export type ReduxProps = {
    currentContract?: Partial<ServiceContract>
    customers?: Customer[]
    isCreatingServiceContract: boolean
    createServiceContractSuccess: boolean
    updateServiceContractSuccess: boolean
    isUpdatingServiceContract: boolean
    isGettingServiceContract: boolean
    error?: ServiceContractError
}

export const TicketSection = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { id: serviceContractId } = useParams<{ id: string }>();

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        currentContract: state.serviceContract.currentContract,
        customers: state.customer.customers || [],
        isCreatingServiceContract: serviceLoading(state, [CREATE_SERVICE_CONTRACT_API]),
        createServiceContractSuccess: state.serviceContract.createServiceContractSuccess,
        isUpdatingServiceContract: serviceLoading(state, [UPDATE_SERVICE_CONTRACT_API]),
        updateServiceContractSuccess: state.serviceContract.updateServiceContractSuccess,
        error: state.serviceContract.error,
        isGettingServiceContract: serviceLoading(state, [GET_CONTRACT_BY_ID_API]),
    }))

    useEffect(() => {
        if (!serviceContractId) {
            dispatch(getCustomersApi())

            const startDate = dayjs()

            form.setFieldsValue({
                startDate,
            })

            dispatch(updateCurrentContract({
                startDate: startDate.format("YYYY-MM-DD"),
            }))
        } else {
            dispatch(getServiceContractByIdApi(Number(serviceContractId)))
        }
    }, [])

    useEffect(() => {
        if (reduxProps.updateServiceContractSuccess && actionButtonClicked) {
            closeModal()

            notification.success({
                message: "Exito",
                description: "Contrato actualizado",
            })

            navigate(Urls.FRONTEND.SERVICE_CONTRACT.SERVICE_CONTRACTS)
        }
    }, [reduxProps.updateServiceContractSuccess])

    useEffect(() => {
        if (reduxProps.currentContract) {
            const rawNSD = reduxProps.currentContract?.nextServiceDebit?.toString() || '';
            const rawSD = reduxProps.currentContract?.startDate?.toString() || '';
            setIsMonthlyContract(reduxProps.currentContract?.isMonthly || false)

            const newParams = {
                isMonthly: reduxProps.currentContract?.isMonthly ? "1" : "0",
                payDate: reduxProps.currentContract?.payDate,
                total: reduxProps.currentContract?.total || 0,
            }

            if (rawNSD.trim().length > 0) {
                if (reduxProps.currentContract?.isMonthly) {
                    newParams["nextServiceDebit"] = dayjs(rawNSD.replace(/Z$/, '')).format('DD-MM-YYYY')
                } else {
                    newParams["noMonthlyPayDate"] = dayjs(rawNSD.replace(/Z$/, ''))
                }
            }
            if (rawSD.trim().length > 0) {
                newParams["startDate"] = dayjs(rawSD.replace(/Z$/, ''))
            }

            form.setFieldsValue(newParams)
        }
    }, [reduxProps.currentContract])

    const [form] = Form.useForm();

    const [isMonthlyContract, setIsMonthlyContract] = useState<boolean>(false)
    const [createCustomerOpen, setCreateCustomerOpen] = useState<boolean>(false)
    const [addPaymentOpen, setAddPaymentOpen] = useState<boolean>(false)
    const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
    const [actionButtonClicked, setActionButtonClicked] = useState<boolean>(false)
    const [modalCancelOpen, setModalCancelOpen] = useState<boolean>(false)
    const [destroyConfirmationOpen, setDestroyConfirmationOpen] = useState<boolean>(false)

    const descriptions = SystemDescriptions.PAGES.SERVICE_CONTRACT.CREATE_SERVICE_CONTRACT
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const renderDetail = () => (
        <Styles.TicketDetailContainer>
            {
                reduxProps.currentContract && reduxProps.currentContract?.detail && reduxProps.currentContract?.detail?.length > 0
                    ? reduxProps.currentContract?.detail?.map(detailItem => <ItemCard
                        key={detailItem.ref}
                        itemDetail={detailItem}
                    />)
                    : <Empty
                        styles={{ image: { height: 60 } }}
                        description={
                            <Typography.Text>
                                {descriptions.EMPTY_STATES.NO_SERVICES_IN_DETAIL}
                            </Typography.Text>
                        }
                    >
                    </Empty>
            }

        </Styles.TicketDetailContainer>
    )

    const shouldButtonBeDisabled = () => {
        const currentContract = { ...reduxProps.currentContract }
        const isDetailValid = currentContract.detail && currentContract.detail.length > 0

        const requiredProperties = ["isMonthly", "total", "payDate", "startDate", "nextServiceDebit"]

        const isDataValid = requiredProperties.every(prop => currentContract.hasOwnProperty(prop) && currentContract[prop] !== undefined);

        return !isDetailValid || !isDataValid || !reduxProps.currentContract?.customer
    }

    const renderFooter = () => (
        <>
            <Divider />
            <Styles.FooterContainer>
                <Styles.TotalContainer>
                    {renderPaymentButton()}
                    <Styles.FooterTotalContainer>
                        <b>{descriptions.TOTAL} {commonDescriptions.CURRENCY_SYMBOL}. </b>
                        <Styles.TotalLabel>{
                            reduxProps?.currentContract?.total
                                ? formatToCurrency(reduxProps?.currentContract?.total?.toString())
                                : '0.00'
                        }</Styles.TotalLabel>
                    </Styles.FooterTotalContainer>
                </Styles.TotalContainer>
                <Styles.FooterActionContainer>
                    <Styles.ActionButton
                        type="primary"
                        disabled={shouldButtonBeDisabled()}
                        onClick={() => {
                            setConfirmationModalOpen(true)
                        }}
                    >
                        {descriptions.SAVE_BUTTON}
                    </Styles.ActionButton>
                </Styles.FooterActionContainer>
            </Styles.FooterContainer>
        </>
    )

    const getNextPaymentDate = (paymentDay: number) => {
        const nextMonth = dayjs().add(1, "month");
        const lastDayOfNextMonth = nextMonth.endOf("month").date();

        const paymentDate = nextMonth.date(Math.min(paymentDay, lastDayOfNextMonth));

        return paymentDate
    };

    const handleStartDateChange = (e: any) => {
        const { isMonthly } = form.getFieldsValue()

        if (!e) {
            form.setFieldsValue({
                startDate: undefined,
                payDate: undefined,
                nextServiceDebit: undefined,
            })

            updateContractData({
                startDate: undefined,
                payDate: undefined,
                nextServiceDebit: undefined,
            })
            return
        }

        if (isMonthly === undefined || isMonthly === null) return;

        const isMonthlyFlag = Number(isMonthly) === 1
        const paymentDay = Number(e.format('D'))
        const startDate = dayjs(e).format('YYYY-MM-DD')
        const nextServiceDebit = isMonthlyFlag ? getNextPaymentDate(paymentDay) : dayjs(e)

        form.setFieldsValue({
            payDate: paymentDay,
            nextServiceDebit: isMonthlyFlag ? nextServiceDebit.format('DD-MM-YYYY') : undefined,
            noMonthlyPayDate: isMonthlyFlag ? undefined : dayjs(e),
        })

        updateContractData({
            startDate: dayjs(startDate).format('YYYY-MM-DD'),
            payDate: paymentDay,
            nextServiceDebit: nextServiceDebit.format('YYYY-MM-DD'),
        })
    }

    const updateContractData = (contractItems: Object) => {
        dispatch(updateCurrentContract(contractItems))
    }

    const openNotification = (message: string, description: string) => {
        notification.error({
            message,
            description,
        });
    };

    const handleIsMonthlyChange = (isMonthly: string) => {
        const isMonthlyFlag: boolean = Number(isMonthly) === 1

        const isAnyDifferentCycleService = reduxProps.currentContract?.detail?.some(item => Boolean(item.isMonthly) !== isMonthlyFlag) || false

        if (isAnyDifferentCycleService) {
            openNotification(
                descriptions.VALIDATIONS.ERROR_TITLE,
                descriptions.VALIDATIONS.SAME_TYPE_SERVICE,
            )

            const currentDetailCycle = Boolean(reduxProps.currentContract?.detail?.[0]?.isMonthly)

            form.setFieldsValue({
                isMonthly: currentDetailCycle ? "1" : "0",
            })
            return
        }

        const { startDate } = form.getFieldsValue()

        setIsMonthlyContract(isMonthlyFlag)

        if (!startDate) {
            updateContractData({
                isMonthly: isMonthlyFlag,
                payDate: undefined,
                nextServiceDebit: undefined,
            })
            return
        }

        const paymentDay = startDate.format('D')
        const nextServiceDebit = isMonthlyFlag ? getNextPaymentDate(paymentDay) : dayjs(startDate)

        form.setFieldsValue({
            payDate: paymentDay,
            nextServiceDebit: nextServiceDebit.format('DD-MM-YYYY'),
        })

        updateContractData({
            startDate: dayjs(startDate).format("YYYY-MM-DD"),
            payDate: paymentDay,
            nextServiceDebit: nextServiceDebit.format('YYYY-MM-DD'),
            isMonthly: isMonthlyFlag,
        })
    }

    const handlePaymentDayChange = (value: number | null) => {
        const { startDate } = form.getFieldsValue()
        if (!startDate || value === null || value < 1 || value > 30) {
            updateContractData({
                payDate: undefined,
            })
            return
        }

        const paymentDay = value
        const nextServiceDebit = getNextPaymentDate(paymentDay)

        form.setFieldsValue({
            payDate: paymentDay,
            nextServiceDebit: nextServiceDebit.format('DD-MM-YYYY'),
        })

        updateContractData({
            startDate,
            payDate: paymentDay,
            nextServiceDebit: nextServiceDebit.format('YYYY-MM-DD'),
        })
    }

    const handleNoMonthlyPaymentDayChange = (e: any) => {
        if (!e) {
            updateContractData({
                payDate: undefined,
                nextServiceDebit: undefined,
            })
            return
        }

        updateContractData({
            payDate: Number(e.format('D')),
            nextServiceDebit: e.format('YYYY-MM-DD'),
        })
    }

    useEffect(() => {
        if (serviceContractId) return

        const detail = reduxProps.currentContract?.detail
        const firstDetail = detail?.[0]
        const hasContractCycle = Object.prototype.hasOwnProperty.call(reduxProps.currentContract || {}, "isMonthly")

        if (!firstDetail || hasContractCycle) return

        const isMonthlyFlag = Boolean(firstDetail.isMonthly)
        const startDate = form.getFieldValue("startDate") || dayjs()
        const noMonthlyPayDate = form.getFieldValue("noMonthlyPayDate") || startDate
        const paymentDay = Number(dayjs(isMonthlyFlag ? startDate : noMonthlyPayDate).format('D'))
        const nextServiceDebit = isMonthlyFlag ? getNextPaymentDate(paymentDay) : dayjs(noMonthlyPayDate)

        setIsMonthlyContract(isMonthlyFlag)

        form.setFieldsValue({
            isMonthly: isMonthlyFlag ? "1" : "0",
            startDate,
            payDate: paymentDay,
            nextServiceDebit: isMonthlyFlag ? nextServiceDebit.format('DD-MM-YYYY') : undefined,
            noMonthlyPayDate: isMonthlyFlag ? undefined : noMonthlyPayDate,
        })

        updateContractData({
            isMonthly: isMonthlyFlag,
            startDate: dayjs(startDate).format("YYYY-MM-DD"),
            payDate: paymentDay,
            nextServiceDebit: nextServiceDebit.format('YYYY-MM-DD'),
        })
    }, [reduxProps.currentContract?.detail?.length])

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        if (e.key === "ADD_PAYMENT") {
            setAddPaymentOpen(true)
        } else if (e.key === "INACTIVATE_CONTRACT") {
            setDestroyConfirmationOpen(true)
        } else if (e.key === "CANCEL") {
            setModalCancelOpen(true)
        }
    };

    const items: MenuProps['items'] = []

    items.push(
        {
            label: reduxProps.currentContract?.payment
                ? descriptions.OPTIONS.EDIT_PAYMENT
                : descriptions.OPTIONS.ADD_PAYMENT,
            key: 'ADD_PAYMENT',
            icon: <CreditCardOutlined />,
            disabled: reduxProps.currentContract?.total === 0 || !reduxProps.currentContract?.detail,
        },
        {
            type: 'divider',
        },
    )

    if (serviceContractId) {
        items.push(
            {
                label: descriptions.OPTIONS.INACTIVATE_CONTRACT,
                key: 'INACTIVATE_CONTRACT',
                icon: <MinusSquareOutlined />,
                danger: true,
                disabled: false,
            },
            {
                type: 'divider',
            },
        )
    }

    items.push(
        {
            label: descriptions.OPTIONS.CANCEL_INPUT,
            key: 'CANCEL',
            danger: true,
            icon: <CloseOutlined />,
        },
    )

    const menuProps = {
        items,
        onClick: handleMenuClick,
    };

    const renderCustomerButton = () => (
        <Button
            type={"primary"}
            shape="round"
            onClick={() => {
                setCreateCustomerOpen(true)
            }}
            icon={reduxProps.currentContract?.customer ? <EditOutlined /> : <UserAddOutlined />}
        >
            {
                reduxProps.currentContract?.customer
                    ? reduxProps.currentContract.customer.name
                    : descriptions.OPTIONS.ADD_CUSTOMER
            }
        </Button>
    )

    const renderPaymentButton = () => (
        reduxProps.currentContract?.payment &&
        <Button
            type={"primary"}
            shape="round"
            onClick={() => {
                setCreateCustomerOpen(true)
            }}
            style={{
                width: 150
            }}
            size='small'
            icon={reduxProps.currentContract?.customer ? <EditOutlined /> : <UserAddOutlined />}
        >
            {descriptions.PAYMENT}
            {formatToCurrency(reduxProps.currentContract?.payment?.amount?.toString() || '0')}
        </Button>
    )

    const renderDataSection = () => (
        <div style={{
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 12,
        }} >
            <Styles.CategoriesContainer>
                {renderCustomerButton()}

                <Dropdown menu={menuProps}>
                    <Button style={{ border: 'none' }} >
                        <Space>
                            <DownOutlined />
                        </Space>
                    </Button>
                </Dropdown>
            </Styles.CategoriesContainer>
            <Divider />
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                autoComplete={'none'}
                size='small'
            >
                <Row gutter={[8, 0]}>
                    <Col12>
                        <Form.Item
                            label={descriptions.FORM.IS_MONTHLY.LABEL}
                            name={"isMonthly"}
                            validateTrigger="onBlur"
                            rules={[
                                getInputRequiredRule(descriptions.FORM.IS_MONTHLY.LABEL)
                            ]}
                        >
                            <Select
                                options={[
                                    {
                                        value: "1",
                                        label: commonDescriptions.YES,
                                    },
                                    {
                                        value: "0",
                                        label: commonDescriptions.NO,
                                    },
                                ]}
                                placeholder={descriptions.FORM.IS_MONTHLY.PLACEHOLDER}
                                onChange={handleIsMonthlyChange}
                            />
                        </Form.Item>
                    </Col12>
                    <Col12>
                        <Form.Item
                            label={descriptions.FORM.START_DATE.LABEL}
                            name={"startDate"}
                            validateTrigger="onBlur"
                            rules={[
                                getInputRequiredRule(descriptions.FORM.START_DATE.LABEL)
                            ]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format={SystemConstants.DATE_FORMAT}
                                disabledDate={(current) => {
                                    let customDate = dayjs().add(-30, 'day')
                                    return current && current < customDate
                                }}
                                onChange={handleStartDateChange}
                            />
                        </Form.Item>
                    </Col12>
                    {
                        isMonthlyContract &&
                        <>
                            <Col12>
                                <Form.Item
                                    label={descriptions.FORM.PAY_DATE.LABEL}
                                    name={"payDate"}
                                    validateTrigger="onBlur"
                                    rules={[
                                        getInputRequiredRule(descriptions.FORM.PAY_DATE.LABEL),
                                        {
                                            validator: (_, value) => {
                                                if (value === undefined || value === null || value === '') {
                                                    return Promise.resolve()
                                                }

                                                if (Number.isInteger(value) && value >= 1 && value <= 30) {
                                                    return Promise.resolve()
                                                }

                                                return Promise.reject(new Error('El dia de pago debe estar entre 1 y 30'))
                                            }
                                        }
                                    ]}
                                >
                                    <InputNumber
                                        min={1}
                                        max={30}
                                        precision={0}
                                        style={{ width: '100%' }}
                                        placeholder={descriptions.FORM.PAY_DATE.PLACEHOLDER}
                                        onChange={handlePaymentDayChange}
                                    />
                                </Form.Item>
                            </Col12>
                            <Col12>
                                <Form.Item
                                    label={descriptions.FORM.NEXT_SERVICE_DEBIT_DATE.LABEL}
                                    name={"nextServiceDebit"}
                                    validateTrigger="onBlur"
                                    rules={[
                                        getInputRequiredRule(descriptions.FORM.NEXT_SERVICE_DEBIT_DATE.LABEL)
                                    ]}
                                >
                                    <Input
                                        placeholder={descriptions.FORM.NEXT_SERVICE_DEBIT_DATE.PLACEHOLDER}
                                        readOnly
                                    />
                                </Form.Item>
                            </Col12>
                        </>
                    }
                    {
                        !isMonthlyContract &&
                        <Col12>
                            <Form.Item
                                label={descriptions.FORM.PAY_DATE.LABEL}
                                name={"noMonthlyPayDate"}
                                validateTrigger="onBlur"
                                rules={[
                                    getInputRequiredRule(descriptions.FORM.PAY_DATE.LABEL)
                                ]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format={SystemConstants.DATE_FORMAT}
                                    disabledDate={(current) => {
                                        let customDate = dayjs().add(-1, 'day')
                                        return current && current < customDate
                                    }}
                                    onChange={handleNoMonthlyPaymentDayChange}
                                />
                            </Form.Item>
                        </Col12>
                    }
                </Row>
            </Form>
        </div>
    )

    const handleCancelAccept = () => {
        dispatch(cleanCurrentContract())
        navigate(Urls.FRONTEND.HOME)
    }

    const closeModal = () => {
        dispatch(cleanCurrentContract())
        setDestroyConfirmationOpen(false)
    }

    const renderModals = () => {
        return <>
            <CreateTicketCustomer
                open={createCustomerOpen}
                onCancel={() => { setCreateCustomerOpen(false) }}
                onAccept={() => { setCreateCustomerOpen(false) }}
                okText={''}
                cancelText={''}
            />
            <CreatePayment
                open={addPaymentOpen}
                onCancel={() => { setAddPaymentOpen(false) }}
                onAccept={() => { setAddPaymentOpen(false) }}
            />
            <ContractConfirmation
                open={confirmationModalOpen}
                onCancel={() => { setConfirmationModalOpen(false) }}
                onAccept={() => {
                    setActionButtonClicked(true)
                    setConfirmationModalOpen(false)

                    if (reduxProps.currentContract) {
                        if (serviceContractId) {
                            dispatch(updateServiceContractApi({
                                ...reduxProps.currentContract as ServiceContract,
                                nextServiceDebit: reduxProps.currentContract.nextServiceDebit?.split("T")[0]!,
                                updateAction: ActivityLogAction.UPDATE,
                            }))
                        } else {
                            dispatch(createServiceContractApi(
                                {
                                    ...reduxProps.currentContract,
                                    isActive: true,
                                } as ServiceContract)
                            )
                        }
                    }
                }}
                okText={descriptions.CONFIRM_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CONFIRM_MODAL.CANCEL_BUTTON}
                currentContract={reduxProps.currentContract as ServiceContract}
                type='SERVICE_CONTRACT'
            />
            <Loader
                isVisible={reduxProps.isCreatingServiceContract || reduxProps.isUpdatingServiceContract || reduxProps.isGettingServiceContract}
            />
            <ModalSuccess
                isVisible={(reduxProps.createServiceContractSuccess) && actionButtonClicked}
                title={descriptions.SUCCESS_MODAL.TITLE}
                description={descriptions.SUCCESS_MODAL.DESCRIPTION}
                onPrimaryAction={handleCancelAccept}
                onSecondaryActiont={handleCancelAccept}
                primaryLabel={descriptions.SUCCESS_MODAL.PRIMARY_LABEL}
                secondaryLabel={descriptions.SUCCESS_MODAL.SECONDARY_LABEL}
                hideSecondaryButton={true}
            />
            <CancelConfirmationModal
                isVisible={modalCancelOpen}
                onAccept={handleCancelAccept}
                onCancel={() => {
                    setModalCancelOpen(false)
                }}
                title={descriptions.CANCEL_MODAL.TITLE}
                okText={descriptions.CANCEL_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CANCEL_MODAL.CANCEL_BUTTON}
                description={descriptions.CANCEL_MODAL.DESCRIPTION}
            />
            <DestroyConfirmationModal
                open={destroyConfirmationOpen}
                title={descriptions.DESTROY.TITLE}
                description={descriptions.DESTROY.DESCRIPTION}
                onCancel={() => {
                    closeModal()
                }}
                onAccept={() => {
                    if (reduxProps.currentContract) {
                        setActionButtonClicked(true)
                        dispatch(updateServiceContractApi({
                            ...reduxProps.currentContract,
                            updateAction: ActivityLogAction.INACTIVATED,
                        } as ServiceContract))
                    }
                }}
                actionLabel={commonDescriptions.ACCEPT}
                destroyKeyLabel={descriptions.DESTROY.FORM.CONFIRMATION.PLACEHOLDER}
                destroyKeyValue={descriptions.DESTROY.INACTIVATE_KEY}
                acceptText={commonDescriptions.ACCEPT}
                cancelText={commonDescriptions.CANCEL}
                isLoading={reduxProps.isUpdatingServiceContract}
                errorMessage={
                    reduxProps.error?.type === ServiceContractErrorOrigin.UPDATE_SERVICE_CONTRACT
                        ? reduxProps.error.detail?.message
                        : undefined
                }
            />
        </>
    }

    return (
        <>
            {renderDataSection()}
            <Divider plain>
                {descriptions.SERVICE_DETAIL}
                {
                    reduxProps.currentContract?.detail?.length && reduxProps.currentContract?.detail?.length > 0
                        ? ` (${reduxProps.currentContract.detail.length})`
                        : ''
                }
            </Divider>
            {renderDetail()}
            {renderFooter()}
            {renderModals()}
        </>
    )
}
